/**
 * Repair requests: an employee reporting that something is wrong with an asset they
 * hold.
 *
 * **A request is NOT a repair, and the difference is the whole point of this file.**
 *
 * A `Repair` is IT's record. It has a vendor, a cost and parts, and opening one takes
 * the asset OUT OF SERVICE - status becomes UNDER_REPAIR and it cannot be issued.
 * That is a decision, and it is IT's to make.
 *
 * A `RepairRequest` is a report. The employee is still using the laptop; nothing has
 * been decided; the asset's status must not move. Folding the two together would mean
 * anyone could take any asset out of service by filling in a form - and the register
 * would start disagreeing with what is physically on people's desks, which is the one
 * thing it exists to get right.
 *
 * So the flow is: employee reports -> IT reviews -> IT either raises a real Repair
 * from it, or declines it with a reason. The request keeps a link to the repair it
 * became, so "what happened about my cracked screen" has an answer.
 */

export const REPAIR_REQUEST_STATUSES = [
  'OPEN',
  'ACKNOWLEDGED',
  'REPAIR_RAISED',
  'DECLINED',
  'RESOLVED',
] as const;
export type RepairRequestStatus = (typeof REPAIR_REQUEST_STATUSES)[number];

export const REPAIR_REQUEST_STATUS_LABELS: Record<RepairRequestStatus, string> = {
  OPEN: 'Open',
  ACKNOWLEDGED: 'Seen by IT',
  REPAIR_RAISED: 'Repair raised',
  DECLINED: 'Declined',
  RESOLVED: 'Resolved',
};

export const REPAIR_REQUEST_STATUS_BLURBS: Record<RepairRequestStatus, string> = {
  OPEN: 'Reported, not yet looked at.',
  ACKNOWLEDGED: 'IT has seen it and is deciding what to do.',
  REPAIR_RAISED: 'It has gone for repair — the asset is out of service.',
  DECLINED: 'IT decided no repair is needed. The reason is on the request.',
  RESOLVED: 'Sorted without a formal repair — a setting, a cable, a reboot.',
};

/** Still needs someone to do something about it. Drives the IT queue and its badge. */
export function isRepairRequestOpen(status: RepairRequestStatus): boolean {
  return status === 'OPEN' || status === 'ACKNOWLEDGED';
}

/**
 * How urgent the reporter says it is.
 *
 * Deliberately the REPORTER'S view, not a priority IT assigns. Someone whose only
 * laptop will not boot cannot work at all, and that is worth knowing before review
 * even if IT later disagrees. Three levels, because a five-level scale collapses to
 * "everything is high" within a month.
 */
export const REPAIR_URGENCIES = ['LOW', 'NORMAL', 'BLOCKING'] as const;
export type RepairUrgency = (typeof REPAIR_URGENCIES)[number];

export const REPAIR_URGENCY_LABELS: Record<RepairUrgency, string> = {
  LOW: 'Minor — can wait',
  NORMAL: 'Affects my work',
  BLOCKING: 'I cannot work at all',
};

export const MAX_REPAIR_REQUEST_DESCRIPTION = 2000;

/**
 * What may be attached to a repair request.
 *
 * Images, because a photo of a cracked hinge settles in one second what three
 * paragraphs cannot - and a PDF, because a vendor's assessment sometimes arrives that
 * way. Deliberately NOT a general file store: no archives, no documents, nothing that
 * runs.
 *
 * The API checks the declared type against the file's own magic bytes; a browser's
 * `Content-Type` on an upload is supplied by the client and means nothing on its own.
 */
export const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'application/pdf',
] as const;
export type AllowedAttachmentType = (typeof ALLOWED_ATTACHMENT_TYPES)[number];

export function isAllowedAttachmentType(mimeType: string): mimeType is AllowedAttachmentType {
  return (ALLOWED_ATTACHMENT_TYPES as readonly string[]).includes(mimeType.toLowerCase());
}

/** Per file. A phone photo is 2-5 MB; 10 covers a burst of detail shots. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Per request. Enough for a few angles, not an album. */
export const MAX_ATTACHMENTS_PER_REQUEST = 5;

/**
 * The extension to store a file under, chosen from the VERIFIED type rather than from
 * the name the browser sent.
 *
 * Never reuse the uploaded filename on disk: it is attacker-controlled, and a name
 * like `../../etc/passwd` or `photo.jpg.html` is the entire class of bug this avoids.
 * The original name is kept in the database for display only.
 */
export function extensionFor(mimeType: AllowedAttachmentType): string {
  switch (mimeType) {
    case 'image/jpeg':
      return 'jpg';
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/heic':
      return 'heic';
    case 'image/heif':
      return 'heif';
    case 'application/pdf':
      return 'pdf';
  }
}

/**
 * Identify a file from its own first bytes.
 *
 * The `Content-Type` on a multipart upload is whatever the client chose to send, so
 * it is a hint and never evidence. A `.jpg` that is really an HTML document, served
 * back later, is stored XSS - and the browser will happily render it if it is allowed
 * to sniff. So the type is decided HERE, from the content, and everything downstream
 * (the stored extension, the Content-Type on the way out) follows from this answer
 * rather than from the upload.
 *
 * Returns null for anything not on the allowlist, which the caller must reject.
 */
export function sniffAttachmentType(bytes: Uint8Array): AllowedAttachmentType | null {
  const at = (i: number) => bytes[i];
  const matches = (offset: number, signature: readonly number[]) =>
    signature.every((byte, i) => at(offset + i) === byte);

  // JPEG: FF D8 FF
  if (matches(0, [0xff, 0xd8, 0xff])) return 'image/jpeg';

  // PNG: 89 "PNG" CR LF SUB LF
  if (matches(0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'image/png';

  // PDF: "%PDF"
  if (matches(0, [0x25, 0x50, 0x44, 0x46])) return 'application/pdf';

  // WEBP: "RIFF" ???? "WEBP" - the size field between them is not fixed.
  if (matches(0, [0x52, 0x49, 0x46, 0x46]) && matches(8, [0x57, 0x45, 0x42, 0x50])) {
    return 'image/webp';
  }

  // HEIC/HEIF: an ISO-BMFF "ftyp" box at offset 4, then a brand. iPhones default to
  // these, so rejecting them would reject the single most likely photo source.
  if (matches(4, [0x66, 0x74, 0x79, 0x70])) {
    const brand = String.fromCharCode(...Array.from(bytes.slice(8, 12)));
    if (['heic', 'heix', 'hevc', 'hevx'].includes(brand)) return 'image/heic';
    if (['mif1', 'msf1'].includes(brand)) return 'image/heif';
  }

  return null;
}
